import React, { FC } from 'react';
import { View, StyleSheet } from 'react-native';

import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import Svg from '@styles/icons/svg';
import { SimpleOnPress } from '@interfaces/base';
import { DashboardScope } from '@api/domain/types';
import type { DashboardResult } from '@api/domain/types';

import DashboardRowWrapper from '@v2/pages/dashboard/common/components/DashboardRowWrapper';
import type { WithBorderRadiusConfig } from '@v2/pages/dashboard/common/types/rows';
import { ROW_LAYOUT } from '@v2/pages/dashboard/common/constants/rows';

import DashboardListBar from '@v2/pages/dashboard/common/components/DashboardList/components/DashboardListBar';
import { DashboardListBarAnchor } from '@v2/pages/dashboard/common/components/DashboardList/components/DashboardListBar/types';
import DashboardListDivergingRowName from './components/DashboardListDivergingRowName';

import { useManageDashboardListDivergingRow } from './hooks/useManageDashboardListDivergingRow';

interface DashboardListDivergingRowProps extends WithColor, SimpleOnPress, WithBorderRadiusConfig {
  scope: DashboardScope;
  item: DashboardResult;
  maxPositive: number | null;
  maxNegative: number | null;
  testID?: string;
}

const DashboardListDivergingRow: FC<DashboardListDivergingRowProps> = ({
  C,
  scope,
  item,
  maxPositive,
  maxNegative,
  onPress,
  borderRadiusConfig,
  testID,
}) => {
  const { seconds, isNegative, valueText, variant, name } = useManageDashboardListDivergingRow({ item });

  const sideMax = isNegative ? maxNegative : maxPositive;

  return (
    <DashboardRowWrapper scope={scope} borderRadiusConfig={borderRadiusConfig} onPress={onPress} testID={testID}>
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
        {isNegative ? (
          <DashboardListBar
            seconds={seconds}
            maxSeconds={sideMax}
            variant={variant}
            anchor={DashboardListBarAnchor.Right}
            valueText={valueText}
          />
        ) : (
          <DashboardListDivergingRowName
            scope={scope}
            side="left"
            name={name}
            imageHash={item.imageHash}
            imageData={item.imageData}
          />
        )}
      </View>

      <View
        style={{
          width: StyleSheet.hairlineWidth,
          alignSelf: 'stretch',
          backgroundColor: C('Hairline/Light/No trans'),
        }}
      />

      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
        {isNegative ? (
          <DashboardListDivergingRowName
            scope={scope}
            side="right"
            name={name}
            imageHash={item.imageHash}
            imageData={item.imageData}
          />
        ) : (
          <DashboardListBar
            seconds={seconds}
            maxSeconds={sideMax}
            variant={variant}
            anchor={DashboardListBarAnchor.Left}
            valueText={valueText}
          />
        )}
      </View>

      <Svg icon="chevronRight" color="Label/Light/Quinary" width={scale(ROW_LAYOUT.chevron.size)} height={scale(ROW_LAYOUT.chevron.size)} />
    </DashboardRowWrapper>
  );
};

export default ColorWrapper(DashboardListDivergingRow);
